window.CloudNineOfferSelector = (root, { catalog, catalogs, defaultSize, defaultColor, gifts, copy, onPreview }) => {
  let selectedColor = defaultColor;
  let selectedSize = defaultSize;
  let selectedBundle = 2;
  const bundleRows = [];
  const configuredRows = new WeakSet();
  const selectorHost = root.querySelector('#packageModal');
  let purchaseSlot = null;
  let openColorDropdown = null;
  const colorDropdowns = new WeakMap();
  const giftValues = {};
  const copyText = (key, values = {}) => Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll('__' + name + '__', String(value)), copy[key]
  );
  const getSelection = () => ({
    count: selectedBundle,
    rows: bundleRows.slice(0, selectedBundle).map(row => ({
      type: row.dataset.hoodieType || 'adult',
      color: row.querySelector('[data-option="color"]').value,
      size: row.querySelector('[data-option="size"]').value
    }))
  });
  const notify = () => {
    root.dispatchEvent(new CustomEvent('grounding:offer-change', {bubbles: true, detail: getSelection()}));
  };
  const reflectSelection = () => root.querySelectorAll("[data-bundle-count]").forEach((card) => {
    const active = Number(card.dataset.bundleCount) === selectedBundle;
    card.classList.toggle("active", active);
    card.querySelector('.offer-heading').setAttribute("aria-checked", String(active));
  });
  const escapeHtml = (value) => String(value).replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
  const catalogFor = row => row?.dataset.hoodieType === 'kids' ? catalogs.kids : catalog;
  const variantFor = (color, size, row) => catalogFor(row).variants.find(
    (variant) => variant.color === color && variant.size === size
  );
  const optionMarkup = (values, selected) => values.map(
    (value) => '<option value="' + escapeHtml(value) + '"' + (value === selected ? " selected" : "") + ">" + escapeHtml(value) + "</option>"
  ).join("");
  const arrow = '<svg class="cn-icon" width="12" height="12" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"/></svg>';
  function colorSwatch(color, row) {
    const style = catalogFor(row).swatches[color];
    return '<span class="hoodie-color-swatch" aria-hidden="true" style="--swatch-color:' + escapeHtml(style.color) + ';--swatch-image:' + escapeHtml(style.image || 'none') + '"></span>';
  }
  function colorOptionMarkup(color, row) {
    return '<div role="option" tabindex="-1" data-color="' + escapeHtml(color) + '">' + colorSwatch(color, row) +
      '<span class="hoodie-color-copy">' + escapeHtml(color) + '</span>' +
      '<span class="hoodie-color-check" aria-hidden="true">✓</span></div>';
  }
  function createColorDropdown(row, number) {
    const select = row.querySelector('select[data-option="color"]');
    const container = row.querySelector('.hoodie-color-dropdown');
    const trigger = container.querySelector('.hoodie-color-trigger');
    const list = container.querySelector('[role="listbox"]');
    const options = [...list.querySelectorAll('[role="option"]')];
    const reflect = () => {
      trigger.innerHTML = colorSwatch(select.value, row) + '<span class="hoodie-color-name">' + escapeHtml(select.value) + '</span>' + arrow;
      trigger.setAttribute('aria-label', copyText('colorFor', {number}) + ': ' + select.value);
      options.forEach(option => option.setAttribute('aria-selected', String(option.dataset.color === select.value)));
    };
    const close = (restoreFocus = false) => {
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (openColorDropdown?.container === container) openColorDropdown = null;
      if (restoreFocus) trigger.focus();
    };
    const open = () => {
      openColorDropdown?.close();
      const rect = trigger.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 88;
      const above = rect.top - 16;
      const flip = below < 240 && above > below;
      list.classList.toggle('is-above', flip);
      list.style.maxHeight = Math.min(288, Math.max(96, flip ? above : below)) + 'px';
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      openColorDropdown = {container, close};
      options.find(option => option.dataset.color === select.value).focus({preventScroll: true});
    };
    trigger.addEventListener('click', () => list.hidden ? open() : close());
    trigger.addEventListener('keydown', event => {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        open();
      }
    });
    options.forEach((option, index) => {
      const choose = () => {
        select.value = option.dataset.color;
        select.dispatchEvent(new Event('change', {bubbles: true}));
        close(true);
      };
      option.addEventListener('click', choose);
      option.addEventListener('keydown', event => {
        if (['Enter', ' '].includes(event.key)) {
          event.preventDefault();
          choose();
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options[next].focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          close(true);
        } else if (event.key === 'Tab') {
          close(true);
        } else if (event.key.length === 1) {
          const match = [...options.slice(index + 1), ...options.slice(0, index + 1)].find(item => item.dataset.color.toLocaleLowerCase().startsWith(event.key.toLocaleLowerCase()));
          match?.focus();
        }
      });
    });
    container.addEventListener('focusout', event => {
      if (!container.contains(event.relatedTarget)) close();
    });
    colorDropdowns.set(row, {reflect});
    reflect();
  }
  document.addEventListener('pointerdown', event => {
    if (openColorDropdown && !openColorDropdown.container.contains(event.target)) openColorDropdown.close();
  });
  window.addEventListener('resize', () => openColorDropdown?.close());
  function syncRow(row) {
    const colorSelect = row.querySelector('select[data-option="color"]');
    const sizeSelect = row.querySelector('select[data-option="size"]');
    const preview = row.querySelector(".compact-hoodie-preview");
    if (!colorSelect || !sizeSelect || !preview) return;
    Array.from(sizeSelect.options).forEach((option) => {
      option.disabled = !variantFor(colorSelect.value, option.value, row)?.availableForSale;
    });
    if (row.hasAttribute('data-size-explicit') && sizeSelect.selectedOptions[0]?.disabled) sizeSelect.value = '';
    if (!row.hasAttribute('data-size-explicit') && sizeSelect.selectedOptions[0]?.disabled) {
      const available = Array.from(sizeSelect.options).find((option) => !option.disabled);
      if (available) sizeSelect.value = available.value;
    }
    colorDropdowns.get(row)?.reflect();
    const variant = variantFor(colorSelect.value, sizeSelect.value, row) || catalogFor(row).variants.find(v => v.color === colorSelect.value);
    if (!variant) return;
    preview.src = variant.imageUrl;
    preview.alt = '';
    row.querySelector('.hoodie-thumbnail-frame').setAttribute('aria-label', copyText('previewColor', {color: colorSelect.value}));
  }
  function renderBundle(count) {
    openColorDropdown?.close();
    const configuration = root.querySelector("#bundleInlineConfig");
    const optionsHost = root.querySelector("#compactHoodieOptions");
    const giftsHost = root.querySelector("#bundleGiftPreview");
    const activeOffer = root.querySelector('.modal-offer-item[data-bundle-count="' + count + '"]');
    if (!configuration || !optionsHost || !giftsHost || !activeOffer) return;
    const giftHeading = copyText('includedGifts', {count: gifts[count].length});
    const giftValueMarkup = giftValues[count]
      ? '<strong class="atf-gift-value">' + escapeHtml(copyText('giftValue', {value: giftValues[count]})) + '</strong>' : '';
    giftsHost.hidden = gifts[count].length === 0;
    const giftList = '<div class="bundle-gift-images">' + gifts[count].map(gift => {
      const digitalLabel = copy.giftDetails[gift.key]?.digital ? copyText('giftDigitalLabel') : '';
      const digitalBadge = digitalLabel ? '<span class="gift-digital-badge">' + escapeHtml(digitalLabel) + '</span>' : '';
      return `
      <button type="button" class="bundle-gift-item gift-preview-trigger"
        data-gift-key="${escapeHtml(gift.key)}" data-gift-name="${escapeHtml(gift.name)}" data-gift-value="${escapeHtml(gift.value)}"
        data-gift-image="${escapeHtml(gift.image)}" aria-label="View ${escapeHtml(gift.name)} details${digitalLabel ? ', ' + escapeHtml(digitalLabel) : ''}">
        <span class="gift-photo"><img src="${escapeHtml(gift.image)}" alt="" width="40" height="40"></span>
        <span class="gift-name">${escapeHtml(gift.name)}<svg class="gift-info-inline" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor"/><path d="M8 7v4M8 4.5v1" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>${digitalBadge}</span>
        <span class="gift-worth"><del>${escapeHtml(gift.value)}</del></span>
      </button>`;
    }).join('') + '</div>';
    giftsHost.innerHTML = '<div class="atf-gift-heading"><strong>' + escapeHtml(giftHeading) + '</strong>' + giftValueMarkup + '</div>' + giftList;
    const currentRows = bundleRows.slice(0, optionsHost.children.length);
    const adultRows = currentRows.filter(row => row.dataset.hoodieType !== 'kids');
    const colors = new Set(adultRows.map(row => row.querySelector('[data-option="color"]').value));
    const sizes = new Set(adultRows.map(row => row.querySelector('[data-option="size"]').value));
    if (colors.size === 1 && catalog.colors.includes([...colors][0])) selectedColor = [...colors][0];
    if (sizes.size === 1 && catalog.sizes.includes([...sizes][0])) selectedSize = [...sizes][0];
    bundleRows.slice(optionsHost.children.length, count).forEach(row => {
      if (configuredRows.has(row)) return;
      row.querySelector('[data-option="color"]').value = selectedColor;
      row.querySelector('[data-option="size"]').value = selectedSize;
      syncRow(row);
    });
    root.querySelectorAll('.modal-offer-item').forEach(card => {
      const tier = Number(card.dataset.bundleCount);
      const giftStrip = card.querySelector('.gifts');
      giftStrip.hidden = !giftValues[tier] || gifts[tier].length === 0;
      giftStrip.innerHTML = giftStrip.hidden ? '' : '<span class="atf-gift-thumbnails" aria-hidden="true">' + gifts[tier].map(gift =>
        '<img src="' + escapeHtml(gift.image) + '" alt="" width="20" height="20">'
      ).join('') + '</span><span>' + escapeHtml(copyText('giftSummary', {count: gifts[tier].length, value: giftValues[tier]})) + '</span>';
    });
    while (bundleRows.length < count) {
      const number = bundleRows.length + 1;
      const variant = variantFor(selectedColor, selectedSize);
      const row = document.createElement('fieldset');
      row.className = 'compact-hoodie-option';
      row.dataset.hoodieType = 'adult';
      const preview = '<button class="hoodie-thumbnail-frame" type="button" aria-haspopup="dialog"><img class="compact-hoodie-preview" src="' + escapeHtml(variant.imageUrl) + '" alt="" width="32" height="40"></button>';
      const colorLabel = copyText('colorFor', {number});
      const colorListId = 'hoodie-colors-' + number;
      const compactFields = '<div class="hoodie-option-line">' + preview + '<div class="hoodie-color-dropdown"><select data-option="color" hidden aria-hidden="true" tabindex="-1">' + optionMarkup(catalog.colors, selectedColor) + '</select><button type="button" class="hoodie-color-trigger" role="combobox" aria-label="' + escapeHtml(colorLabel) + '" aria-haspopup="listbox" aria-expanded="false" aria-controls="' + colorListId + '"></button><div class="hoodie-color-list" role="listbox" id="' + colorListId + '" aria-label="' + escapeHtml(colorLabel) + '" hidden>' + catalog.colors.map(color => colorOptionMarkup(color, row)).join('') + '</div></div><label class="compact-select-wrap"><select data-option="size" aria-label="' + escapeHtml(copyText('sizeFor', {number})) + '">' + optionMarkup(catalog.sizes, selectedSize) + '</select>' + arrow + '</label></div>';
      row.innerHTML = '<legend class="hoodie-row-label">' + escapeHtml(copyText('hoodieNumber', {number})) + '</legend>' + compactFields;
      row.addEventListener('change', event => {
        const select = event.target.closest('select');
        if (!select) return;
        configuredRows.add(row);
        syncRow(row);
        if (selectedBundle === 1 && select.dataset.option === "size") selectedSize = select.value;
        notify();
      });
      createColorDropdown(row, number);
      if (catalogs?.kids?.variants.length) {
        const choice = document.createElement('label');
        choice.className = 'hoodie-kids-choice';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.setAttribute('aria-label', copy.kidsFor.replace('__number__', number));
        const label = document.createElement('span');
        label.textContent = copy.kidsChoice;
        choice.append(checkbox, label);
        const note = document.createElement('p');
        note.className = 'hoodie-family-note';
        note.setAttribute('role', 'status');
        note.hidden = true;
        row.append(choice, note);
        checkbox.addEventListener('change', () => {
          openColorDropdown?.close();
          const previousColor = row.querySelector('[data-option="color"]').value;
          row.dataset.hoodieType = checkbox.checked ? 'kids' : 'adult';
          row.setAttribute('data-size-explicit', '');
          const next = catalogFor(row);
          const color = next.colors.includes(previousColor) ? previousColor : next.colors[0];
          const dropdown = row.querySelector('.hoodie-color-dropdown');
          const replacement = dropdown.cloneNode(true);
          replacement.querySelector('select').innerHTML = optionMarkup(next.colors, color);
          replacement.querySelector('[role="listbox"]').innerHTML = next.colors.map(value => colorOptionMarkup(value, row)).join('');
          dropdown.replaceWith(replacement);
          const size = row.querySelector('[data-option="size"]');
          size.innerHTML = '<option value="" selected disabled>' + escapeHtml(copy.chooseSize) + '</option>' + optionMarkup(next.sizes, '');
          size.setAttribute('aria-label', (checkbox.checked ? copy.kidsSize : copy.adultSize) + ' · ' + copyText('hoodieNumber', {number}));
          row.querySelector('.hoodie-row-label').textContent = copyText('hoodieNumber', {number}) + ' · ' + (checkbox.checked ? copy.kidsSize : copy.adultSize);
          note.hidden = color === previousColor;
          note.textContent = color === previousColor ? '' : copyText('colorChanged', {color});
          configuredRows.add(row);
          createColorDropdown(row, number);
          syncRow(row);
          notify();
        });
      }
      row.querySelector('.hoodie-thumbnail-frame').addEventListener('click', event => {
        const color = row.querySelector('[data-option="color"]').value;
        const size = row.querySelector('[data-option="size"]').value;
        onPreview({color, size, type: row.dataset.hoodieType, mediaId: (variantFor(color, size, row) || catalogFor(row).variants.find(v => v.color === color)).imageId, trigger: event.currentTarget});
      });
      syncRow(row);
      bundleRows.push(row);
    }
    if (optionsHost.children.length !== count) optionsHost.replaceChildren(...bundleRows.slice(0, count));
    activeOffer.append(configuration, giftsHost, purchaseSlot, root.querySelector('#groundingPurchaseReassurance'));
    configuration.querySelector('.configure-head h3').textContent = copy.configure;
    configuration.hidden = false;
  }
  root.querySelectorAll(".modal-offer-item[data-bundle-count]").forEach((offer) => {
    offer.addEventListener("click", (event) => {
      if (event.target.closest("select, button, input, textarea, a, .bundle-inline-config")) return;
      const count = Number(offer.dataset.bundleCount);
      if (![1, 2, 4].includes(count) || count === selectedBundle) return;
      selectedBundle = count;
      reflectSelection();
      renderBundle(count);
      notify();
    });
  });
  root.querySelectorAll("[data-bundle-count]").forEach((card) => {
    card.addEventListener("keydown", (event) => {
      if ((event.target === card || event.target === card.querySelector('.offer-heading')) && [" ", "Enter"].includes(event.key)) {
        event.preventDefault();
        card.click();
      }
    });
  });
  function initializeLayout() {
    purchaseSlot = document.createElement('div');
    purchaseSlot.className = 'atf-purchase-slot';
    const purchaseBar = document.createElement('div');
    purchaseBar.className = 'atf-purchase-bar';
    purchaseBar.append(root.querySelector('#modalConfirmBtn'));
    purchaseSlot.append(purchaseBar);
    selectorHost.append(purchaseSlot);
    const mobile = window.matchMedia('(max-width: 900px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let purchaseVisible = false;
    let purchaseSeen = false;
    let docked = false;
    let purchaseAnimation = null;
    const preservePurchaseSpace = () => {
      document.body.style.setProperty('--grounding-purchase-height', `${purchaseBar.offsetHeight}px`);
      if (!purchaseBar.classList.contains('is-docked')) {
        purchaseSlot.style.minHeight = `${purchaseBar.offsetHeight}px`;
      }
    };
    new ResizeObserver(preservePurchaseSpace).observe(purchaseBar);
    const animatePurchaseBar = (keyframes, duration, onFinish) => {
      const animation = purchaseBar.animate(keyframes, {
        duration,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both'
      });
      purchaseAnimation = animation;
      animation.onfinish = () => {
        if (purchaseAnimation !== animation) return;
        purchaseAnimation = null;
        animation.cancel();
        onFinish?.();
      };
    };
    const reflectPurchaseBar = () => {
      const nextDocked = mobile.matches && purchaseSeen && purchaseSlot.getBoundingClientRect().bottom <= 0;
      if (nextDocked === docked) return;
      docked = nextDocked;
      const interrupted = purchaseAnimation !== null;
      const currentStyle = interrupted ? getComputedStyle(purchaseBar) : null;
      const opacity = currentStyle?.opacity ?? '1';
      const transform = currentStyle?.transform ?? 'translateY(0)';
      purchaseAnimation?.cancel();
      purchaseAnimation = null;
      preservePurchaseSpace();
      if (!mobile.matches || reducedMotion.matches) {
        purchaseBar.classList.toggle('is-docked', docked);
      } else if (docked) {
        purchaseBar.classList.add('is-docked');
        animatePurchaseBar([
          {opacity: interrupted ? opacity : 0, transform: interrupted ? transform : 'translateY(12px)'},
          {opacity: 1, transform: 'translateY(0)'}
        ], 220);
      } else {
        animatePurchaseBar([
          {opacity, transform},
          {opacity: 0, transform: 'translateY(12px)'}
        ], 140, () => {
          purchaseBar.classList.remove('is-docked');
          if (purchaseVisible) {
            animatePurchaseBar([{opacity: 0}, {opacity: 1}], 140);
          }
        });
      }
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        purchaseVisible = entry.intersectionRatio === 1;
        if (mobile.matches && purchaseVisible) purchaseSeen = true;
      });
      reflectPurchaseBar();
    }, {threshold: [0, 1]});
    observer.observe(purchaseSlot);
    let scrollFrame = null;
    window.addEventListener('scroll', () => {
      if (scrollFrame !== null) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null;
        reflectPurchaseBar();
      });
    }, {passive: true});
    mobile.addEventListener('change', reflectPurchaseBar);
    reducedMotion.addEventListener('change', () => {
      purchaseAnimation?.cancel();
      purchaseAnimation = null;
      purchaseBar.classList.toggle('is-docked', docked);
    });
    selectorHost.querySelectorAll('.modal-offer-item').forEach(card => {
      const heading = card.querySelector('.offer-heading');
      heading.setAttribute('role', 'radio');
      heading.setAttribute('tabindex', '0');
      heading.setAttribute('aria-checked', card.getAttribute('aria-checked'));
      card.setAttribute('role', 'group');
      card.removeAttribute('tabindex');
      card.removeAttribute('aria-checked');
      card.setAttribute('aria-labelledby', 'atf-quantity-' + card.dataset.bundleCount);
      const indicator = document.createElement('span');
      indicator.className = 'atf-choice-indicator';
      indicator.setAttribute('aria-hidden', 'true');
      const quantity = card.querySelector('.quantity');
      quantity.id = 'atf-quantity-' + card.dataset.bundleCount;
      quantity.prepend(indicator);
      if (card.dataset.bundleCount === '1') card.querySelector('.tag').remove();
      if (card.dataset.bundleCount === '4') card.querySelector('.tag').textContent = copy.limitedTimeLabel;
      card.querySelector('.top').append(card.querySelector('[data-offer-savings]'));
      card.querySelector('.price-group').append(card.querySelector('[data-offer-unit]'));
    });
  }
  initializeLayout();
  reflectSelection();
  renderBundle(selectedBundle);
  return {
    getSelection,
    setDefaultColor(color) {
      selectedColor = color;
      bundleRows.forEach((row) => {
        if (configuredRows.has(row)) return;
        row.querySelector('[data-option="color"]').value = color;
        syncRow(row);
      });
      notify();
    },
    setGifts(nextGifts, nextValues) {
      Object.assign(gifts, nextGifts);
      Object.assign(giftValues, nextValues);
      renderBundle(selectedBundle);
    },
    setRow(index, option, value) {
      const row = bundleRows[index];
      if (!row) return;
      configuredRows.add(row);
      row.querySelector('select[data-option="' + option + '"]').value = value;
      syncRow(row);
      notify();
    }
  };
};
